import React, { useContext } from 'react'

import { faFlaskGear, faGlobeEurope, faSignOutAlt, faUserCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Dropdown, Menu, Tooltip } from 'antd'
import { bindActionCreators } from 'redux'
import Cookie from 'js-cookie'
import { connect } from 'react-redux'
import { addTranslationForLanguage, getActiveLanguage, getLanguages, getTranslate, setActiveLanguage } from 'react-localize-redux'

import { setLanguage } from '../../utils/locale'
import { getUser, updateUserProfile } from '../../reducers/UserReducer'
import { buildUpdatedUserFromResponse, updateUser } from '../../utils/api/user'
import { GlobalContext } from '../../Providers/GlobalProvider'
import { ColorsPalette } from '../../config/colors'
import { Cookies } from '../../utils/Cookie'

const mapStateToProps = state => ({
  currentLanguage: getActiveLanguage(state.locale).code,
  languages: getLanguages(state.locale),
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

const mapDispatchToProps = dispatch => ({
  addTranslation: bindActionCreators(addTranslationForLanguage, dispatch),
  setActiveLanguage: bindActionCreators(setActiveLanguage, dispatch),
  updateUserProfile: bindActionCreators(updateUserProfile, dispatch)
})

const LanguagesMenu = ({ currentLanguage, icon, languages, title, type, user, t, addTranslation, onLogout, onSwitchProfile, setActiveLanguage, updateUserProfile }) => {
  const { invalidateEventCodes } = useContext(GlobalContext)
  const { betaMode } = useContext(GlobalContext)

  const updateUserLanguage = language => {
    if (user.id && user.language !== language) {
      updateUser(user, { id: user.id, language }).then(json => {
        // New contract: the response returns { account, profiles } (no `data`). The current profile
        // is rebuilt with the account identity.
        const updatedUser = buildUpdatedUserFromResponse(json, user.id)

        if (!updatedUser) { return }

        updateUserProfile(updatedUser)
        invalidateEventCodes()
        if (
          typeof Cookie.get('user') !== 'undefined' &&
            Cookie.get('user') !== 'undefined' &&
            typeof Cookie.get('token') !== 'undefined' &&
            Cookie.get('token') !== 'undefined'
        ) {
          Cookie.set(Cookies.USER, JSON.stringify(updatedUser), { expires: 7 })
        }
      })
    }
  }

  /**
   * Translated role(s) of a profile; two profiles can carry the same role in different
   * organizations, in which case the organization (2nd line) is what tells them apart.
   */
  const getProfileRolesLabel = profile => {
    const roles = (Array.isArray(profile.roles) ? profile.roles : []).filter(r => r !== 'ROLE_USER')

    return roles.map(r => t(r)).join(', ')
  }

  const getProfileOrganizationsLabel = profile => {
    return [
      profile.school ? profile.school.name : null,
      ...(Array.isArray(profile.institutions) ? profile.institutions.map(i => i.name) : [])
    ].filter(o => o).join(', ')
  }

  const renderProfilesItems = () => {
    const profiles = Array.isArray(user?.profiles) ? user.profiles : []

    // The section is displayed even with a single profile (which is then simply marked active).
    // Under admin impersonation the list holds the profiles of the impersonated user: switching then
    // moves the impersonation (?_switch_user) to the other profile.
    if (!onSwitchProfile || profiles.length === 0) { return [] }

    return [
      <Menu.ItemGroup key='profiles-group' title={t('profile_switcher.my_profiles')}>
        {profiles.map(profile => {
          const isActive = profile.id === user.id
          const organizations = getProfileOrganizationsLabel(profile)

          return (
            <Menu.Item
              key={`profile-${profile.id}`}
              onClick={() => { if (!isActive) { onSwitchProfile(profile.id) } }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon
                  icon={faUserCircle}
                  fixedWidth
                  style={{ color: isActive ? ColorsPalette.BLUE_ICON : 'transparent', marginRight: '8px' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '220px' }}>
                  <span style={{ lineHeight: '20px' }}>
                    {getProfileRolesLabel(profile) || (profile.username ?? profile.email)}
                  </span>
                  {organizations && (
                    <Tooltip placement='left' title={organizations}>
                      <span
                        style={{
                          lineHeight: '16px',
                          fontSize: '12px',
                          color: 'rgba(0, 0, 0, 0.45)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {organizations}
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            </Menu.Item>
          )
        })}
      </Menu.ItemGroup>,
      <Menu.Divider key='menu-divider-profiles' />
    ]
  }

  const renderMenu = () => {
    const menuItems = []

    if (onLogout) {
      menuItems.push(
        ...renderProfilesItems(),
        <Menu.Item key='menu-item-2' onClick={onLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} color={ColorsPalette.BLUE_ICON} />
          {t('Log out')}
        </Menu.Item>,
        <Menu.Divider key='menu-divider-2' />
      )

      if (betaMode.available) {
        menuItems.push(
          <Menu.Item key='menu-item-3' onClick={() => betaMode.toggle()}>
            <FontAwesomeIcon icon={faFlaskGear} color={betaMode.active ? ColorsPalette.LIGHT_MOSS_GREEN : ColorsPalette.BLUE_ICON} />
            {t('global_settings.beta_mode')}
          </Menu.Item>,
          <Menu.Divider key='menu-divider-3' />
        )
      }
    }

    languages.forEach(l => {
      menuItems.push(
        <Menu.Item
          key={l.code} onClick={e => {
            setLanguage({
              language: e.key,
              addTranslationForLanguage: addTranslation,
              setActiveLanguage,
              getActiveLanguage,
              getUser: () => { return user }
            })
            updateUserLanguage(e.key)
          }}
          className='ant-dropdown-menu-item'
        >
          <FontAwesomeIcon
            icon={faGlobeEurope}
            style={{
              color: currentLanguage === l.code
                ? ColorsPalette.BLUE_ICON
                : 'transparent'
            }}
          />
          {t(l.name)}
        </Menu.Item>
      )
    })

    return (
      <Menu>
        {menuItems}
      </Menu>
    )
  }

  return (
    <Dropdown
      overlay={renderMenu()}
      trigger={['click']}
    >
      <Tooltip placement='left' title={title}>
        <Button type={type ?? 'default'}>
          <FontAwesomeIcon icon={icon} />
        </Button>
      </Tooltip>
    </Dropdown>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(LanguagesMenu)
